import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { CATEGORIES } from './UploadTemplateDialog';
import {
  Save, Loader2, Upload, FileText, FileSpreadsheet, History, Settings2, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const EditTemplateDialog = ({ open, onOpenChange, template, onSaved }) => {
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', category: 'Allgemein', description: '' });
  const [savingMeta, setSavingMeta] = useState(false);
  const [versionFile, setVersionFile] = useState(null);
  const [versionNote, setVersionNote] = useState('');
  const [uploadingVersion, setUploadingVersion] = useState(false);

  useEffect(() => {
    if (template) {
      setForm({
        name: template.name || '',
        category: template.category || 'Allgemein',
        description: template.description || '',
      });
      setVersionFile(null);
      setVersionNote('');
    }
  }, [template]);

  if (!template) return null;

  const versions = [...(template.versions || [])].sort((a, b) => (b.version || 0) - (a.version || 0));

  const handleSaveMeta = async () => {
    if (!form.name.trim()) {
      toast.error('Name darf nicht leer sein');
      return;
    }
    setSavingMeta(true);
    try {
      await api.patch(`/templates/${template.id}`, {
        name: form.name,
        category: form.category,
        description: form.description,
      });
      toast.success('Stammdaten gespeichert');
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleVersionSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (ext !== template.file_type) {
      toast.error(`Die neue Version muss eine .${template.file_type}-Datei sein`);
      return;
    }
    setVersionFile(file);
  };

  const handleUploadVersion = async () => {
    if (!versionFile) {
      toast.error('Bitte eine Datei auswählen');
      return;
    }
    setUploadingVersion(true);
    try {
      const formData = new FormData();
      formData.append('file', versionFile);
      await api.post(`/templates/${template.id}/version`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: { note: versionNote || '' },
      });
      toast.success('Neue Version gespeichert');
      setVersionFile(null);
      setVersionNote('');
      onSaved?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingVersion(false);
    }
  };

  const handleDownloadVersion = async (version) => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/templates/${template.id}/versions/${version}/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('Download fehlgeschlagen');
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${template.name}_v${version}.${template.file_type}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch {
      toast.error('Fehler beim Herunterladen');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Settings2 className="h-5 w-5 text-primary" />
            Vorlage bearbeiten
          </DialogTitle>
          <DialogDescription>
            Stammdaten ändern oder eine neue Version hochladen — ohne Herunterladen.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="details" className="pt-2">
          <TabsList className="w-full">
            <TabsTrigger value="details" className="flex-1" data-testid="edit-tab-details">
              <Settings2 className="h-4 w-4 mr-1" /> Details
            </TabsTrigger>
            <TabsTrigger value="version" className="flex-1" data-testid="edit-tab-version">
              <Upload className="h-4 w-4 mr-1" /> Neue Version
            </TabsTrigger>
            <TabsTrigger value="history" className="flex-1" data-testid="edit-tab-history">
              <History className="h-4 w-4 mr-1" /> Historie ({versions.length})
            </TabsTrigger>
          </TabsList>

          {/* DETAILS */}
          <TabsContent value="details" className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name *</Label>
              <Input
                id="edit-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="edit-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Kategorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-desc">Beschreibung</Label>
              <Textarea
                id="edit-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Optionale Beschreibung..."
                data-testid="edit-desc-input"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
                Schließen
              </Button>
              <Button onClick={handleSaveMeta} disabled={savingMeta} className="rounded-full" data-testid="save-meta-btn">
                {savingMeta ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                Speichern
              </Button>
            </div>
          </TabsContent>

          {/* NEW VERSION */}
          <TabsContent value="version" className="space-y-4 pt-4">
            <p className="text-sm text-muted-foreground">
              Lade eine überarbeitete <span className="font-mono uppercase">.{template.file_type}</span>-Datei hoch.
              Die bisherige Version bleibt mit Bearbeiter in der Historie erhalten.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept={`.${template.file_type}`}
              onChange={handleVersionSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-border rounded-lg p-6 text-center cursor-pointer hover:bg-secondary/30 transition-colors"
              data-testid="version-file-btn"
            >
              {versionFile ? (
                <div className="flex items-center justify-center gap-2">
                  {template.file_type === 'docx'
                    ? <FileText className="h-8 w-8 text-blue-500" />
                    : <FileSpreadsheet className="h-8 w-8 text-green-500" />}
                  <div className="text-left">
                    <p className="font-medium">{versionFile.name}</p>
                    <p className="text-xs text-muted-foreground">{(versionFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">Klicken zum Auswählen</p>
                  <p className="text-xs text-muted-foreground mt-1">.{template.file_type} (max. 10MB)</p>
                </>
              )}
            </button>
            <div className="space-y-2">
              <Label htmlFor="version-note">Änderungshinweis (optional)</Label>
              <Input
                id="version-note"
                value={versionNote}
                onChange={(e) => setVersionNote(e.target.value)}
                placeholder="z.B. Logo aktualisiert, Klausel §3 angepasst"
                data-testid="version-note-input"
              />
            </div>
            <div className="flex justify-end pt-2">
              <Button onClick={handleUploadVersion} disabled={uploadingVersion || !versionFile} className="rounded-full" data-testid="upload-version-btn">
                {uploadingVersion ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                Neue Version speichern
              </Button>
            </div>
          </TabsContent>

          {/* HISTORY */}
          <TabsContent value="history" className="pt-4">
            <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1" data-testid="version-history-list">
              {versions.map((v) => (
                <div
                  key={v.version}
                  className="flex items-start justify-between gap-3 border border-border/60 rounded-lg p-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge variant={v.version === template.version ? 'default' : 'secondary'} className="text-xs">
                        v{v.version}{v.version === template.version ? ' · aktuell' : ''}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{formatDate(v.uploaded_at)}</span>
                    </div>
                    <p className="text-sm mt-1 truncate">
                      {v.uploaded_by || 'Unbekannt'}
                    </p>
                    {v.note && <p className="text-xs text-muted-foreground mt-0.5">{v.note}</p>}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="rounded-full shrink-0"
                    onClick={() => handleDownloadVersion(v.version)}
                    title="Diese Version herunterladen"
                    data-testid={`download-version-${v.version}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
